import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card} from 'antd';
import {AnchorLink, EmailLink, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Select, SubmitButton} from 'formik-antd';
import React, {useState} from 'react';
import {SectionProps} from '../utils/types';
import {AffiliateFieldsFragment, useUpdateAffiliateTierMutation} from 'api/generated';
import {formatAffiliateTier} from 'routes/Affiliates/Affiliates';
import {formatDate, formatReferralLink} from 'lib/formaters';

export const AffiliateDetails: React.FC<SectionProps> = ({cardProps, formProps, affiliate}) => {
  const [isEditing, setEditing] = useState(false);
  const [updateAffiliateTier] = useUpdateAffiliateTierMutation();

  if (!affiliate)
    return (
      <Card {...cardProps} title="Affiliate details">
        <PropertyList omitEmpty properties={[]} />
      </Card>
    );

  const handleSubmit = async (values: AffiliateFieldsFragment) => {
    if (values.meta_data?.tier !== affiliate.meta_data?.tier) {
      await updateAffiliateTier({
        variables: {
          input: {
            affiliateId: affiliate.id,
            tier: values.meta_data?.tier
          }
        }
      });
    }
    setEditing(false);
  };

  const initialValues: AffiliateFieldsFragment = {
    id: affiliate.id,
    meta_data: affiliate.meta_data,
    affiliate_group_id: affiliate.affiliate_group_id
  };

  return (
    <>
      <Card
        {...cardProps}
        title="Affiliate details"
        extra={
          isEditing ? null : (
            <AnchorLink onClick={() => setEditing(true)}>
              <EditOutlined /> Edit data
            </AnchorLink>
          )
        }>
        {isEditing ? (
          <Formik onSubmit={handleSubmit} initialValues={initialValues}>
            <Form {...formProps}>
              <FormItem name="meta_data.tier" label="Tier">
                <Select name="meta_data.tier" placeholder="Tier" defaultValue={'2'}>
                  <Select.Option value="1">Platinum</Select.Option>
                  <Select.Option value="2">Gold</Select.Option>
                </Select>
              </FormItem>
              <FormItem
                wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                name="submit">
                <Stack>
                  <Button onClick={() => setEditing(false)}>Cancel</Button>
                  <SubmitButton>Submit</SubmitButton>
                </Stack>
              </FormItem>
            </Form>
          </Formik>
        ) : (
          <PropertyList
            omitEmpty
            properties={[
              {
                label: 'ID',
                value: affiliate.id
              },
              {
                label: 'Name',
                value: [affiliate.firstname, affiliate.lastname].filter(Boolean).join(' ') || 'N/A'
              },
              {
                label: 'Email',
                value: <EmailLink email={affiliate.email} />,
                copyable: {text: affiliate.email || 'N/A'}
              },
              {
                label: 'Tier',
                value: formatAffiliateTier(affiliate.meta_data?.tier || '2')
              },
              {
                label: 'Registration date',
                value: formatDate(affiliate.created_at)
              },
              {
                label: 'Referral code',
                copyable: {text: formatReferralLink(affiliate.referral_link?.link) || ''},
                value: formatReferralLink(affiliate.referral_link?.link)
              },
              {
                label: 'Referral link',
                copyable: {text: affiliate.referral_link?.link || ''},
                value: affiliate.referral_link?.link
              }
            ]}
          />
        )}
      </Card>
    </>
  );
};
